import React, { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { RotateCcw, Sparkles, FlaskConical, HeartPulse, Bookmark } from "lucide-react";

const MIN_AGE = 20, MAX_AGE = 90, CX = 110, CY = 104, R = 84;
const pt = (a, r) => { const x = (Math.PI / 180) * a; return [CX + r * Math.cos(x), CY - r * Math.sin(x)]; };
const angleForAge = (age) => { const c = Math.max(MIN_AGE, Math.min(age, MAX_AGE)); return 180 * (1 - (c - MIN_AGE) / (MAX_AGE - MIN_AGE)); };
const arcPath = (s, e, r) => {
  const [x1, y1] = pt(s, r), [x2, y2] = pt(e, r);
  const la = Math.abs(e - s) > 180 ? 1 : 0; const sw = s > e ? 1 : 0;
  return `M ${x1} ${y1} A ${r} ${r} 0 ${la} ${sw} ${x2} ${y2}`;
};
const toneColor = (age, chrono) => {
  if (age == null || chrono == null) return "#B07D2B";
  const gap = age - chrono;
  return gap <= -0.5 ? "#3F6B4C" : gap >= 2 ? "#C05C42" : "#B07D2B";
};

function Dial({ value, label, color, refValue, testid }) {
  const show = value != null;
  const ang = show ? angleForAge(value) : 90;
  const [bx, by] = pt(ang, R);
  return (
    <svg viewBox="0 0 220 132" className="w-[200px] max-w-full" role="img" aria-label={label} data-testid={testid}>
      <path d={arcPath(180, 0, R)} fill="none" stroke="#E2D8C8" strokeWidth="13" strokeLinecap="round" />
      {show && <path d={arcPath(180, ang, R)} fill="none" stroke={color} strokeWidth="13" strokeLinecap="round" />}
      {refValue != null && (() => {
        const [ix, iy] = pt(angleForAge(refValue), R - 10);
        const [ox, oy] = pt(angleForAge(refValue), R + 10);
        return <line x1={ix} y1={iy} x2={ox} y2={oy} stroke="#2D322B" strokeWidth="2" strokeLinecap="round" />;
      })()}
      {show && <circle cx={bx} cy={by} r="7" fill={color} stroke="#F9F6F0" strokeWidth="2.5" />}
      <text x={CX} y={CY - 6} textAnchor="middle" fontSize="42" fontFamily="Georgia, serif" fill="#2D322B">{show ? value : "—"}</text>
      <text x={CX} y={CY + 14} textAnchor="middle" fontSize="9" letterSpacing="1" fill="#5C6359">{label}</text>
    </svg>
  );
}

const SYMPTOMS = [
  { key: "energy", label: "Energy" }, { key: "sleep", label: "Sleep" }, { key: "mood", label: "Mood" },
  { key: "digestion", label: "Digestion" }, { key: "recovery", label: "Recovery" },
];
const HABIT_LABELS = { exercise: "Exercise", diet: "Diet", smoking: "Smoking", alcohol: "Alcohol", stress: "Stress", sleep_hours: "Sleep hours" };
const OPT_LABEL = { lt5: "< 5 hrs", "5-6": "5–6 hrs", "7-8": "7–8 hrs", gt8: "> 8 hrs" };
const optLabel = (v) => OPT_LABEL[v] || (v ? v.charAt(0).toUpperCase() + v.slice(1) : "Not set");

export default function DigitalTwin({ data, onSaved }) {
  const chrono = data.chronological_age;
  const hasLabs = data.estimate_source === "labs";
  const baseBiomarker = data.biological_age;
  const habitOptions = data.habit_options || {};
  const savedSymptoms = data.lifestyle?.symptoms || {};
  const savedHabits = data.lifestyle?.habits || {};
  const phenoInputs = data.pheno_inputs || [];

  const initSymptoms = () => SYMPTOMS.reduce((a, s) => ({ ...a, [s.key]: savedSymptoms[s.key] || 3 }), {});
  const initHabits = () => Object.keys(habitOptions).reduce((a, k) => ({ ...a, [k]: savedHabits[k] || "" }), {});
  const initBio = () => phenoInputs.reduce((a, p) => ({ ...a, [p.slot]: p.value }), {});

  const [symptoms, setSymptoms] = useState(initSymptoms);
  const [habits, setHabits] = useState(initHabits);
  const [bio, setBio] = useState(initBio);
  const [twin, setTwin] = useState({
    biomarker_bioage: baseBiomarker,
    lifestyle_adjusted_bioage: data.lifestyle_adjusted_bioage,
    lifestyle_modifier: data.lifestyle_modifier ?? 0,
    lifestyle_factors: data.lifestyle_factors || [],
  });
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const timer = useRef(null);

  const recompute = useCallback((s, h, b) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const cleanHabits = Object.fromEntries(Object.entries(h).filter(([, v]) => v));
        const { data: t } = await api.post("/bioage/twin", { biomarkers: b, lifestyle: { symptoms: s, habits: cleanHabits } });
        setTwin(t);
      } catch { /* keep last good */ }
    }, 350);
  }, []);

  useEffect(() => () => timer.current && clearTimeout(timer.current), []);

  const setSym = (k, v) => { const s = { ...symptoms, [k]: v }; setSymptoms(s); recompute(s, habits, bio); };
  const setHab = (k, v) => { const h = { ...habits, [k]: v }; setHabits(h); recompute(symptoms, h, bio); };
  const setBioV = (k, v) => { const b = { ...bio, [k]: v }; setBio(b); recompute(symptoms, habits, b); };
  const reset = () => { const s = initSymptoms(), h = initHabits(), b = initBio(); setSymptoms(s); setHabits(h); setBio(b); recompute(s, h, b); };

  const saveTwin = async () => {
    setSaving(true);
    try {
      const scenario = {
        symptoms,
        habits: Object.fromEntries(Object.entries(habits).filter(([, v]) => v)),
        biomarkers: bio,
      };
      await api.post("/bioage/twins", { label: label.trim() || undefined, scenario });
      toast.success("Twin saved. Track your progress below.");
      setLabel("");
      onSaved && onSaved();
    } catch {
      toast.error("Could not save this twin. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const bmk = twin.biomarker_bioage;
  const adj = twin.lifestyle_adjusted_bioage;
  const mod = twin.lifestyle_modifier ?? 0;
  const adjGap = adj != null && chrono != null ? Math.round((adj - chrono) * 10) / 10 : null;

  return (
    <div className="bg-[#F1ECE4] border border-[#D1C7B8] rounded-3xl p-8 sm:p-10" data-testid="digital-twin">
      <div className="flex items-center gap-3 mb-2">
        <Sparkles className="w-5 h-5 text-[#C05C42]" />
        <p className="label-eyebrow">Digital Twin</p>
      </div>
      <p className="font-serif text-2xl text-[#2D322B] leading-snug max-w-2xl">
        Explore an alternate version of you. Slide your habits, symptoms and biomarkers to see how your biological age could shift.
      </p>

      {/* Two dials */}
      <div className="mt-8 grid sm:grid-cols-2 gap-6">
        <div className="rounded-2xl bg-[#F9F6F0] border border-[#D1C7B8] p-5 flex flex-col items-center" data-testid="twin-dial-biomarker">
          <div className="flex items-center gap-2 mb-1 text-[#5C6359]"><FlaskConical className="w-4 h-4 text-[#C05C42]" /><span className="text-xs uppercase tracking-widest">Biomarker Bio-Age</span></div>
          <Dial value={bmk} label="FROM YOUR LABS" color={toneColor(bmk, chrono)} refValue={hasLabs ? baseBiomarker : null} testid="twin-dial-biomarker-svg" />
          <p className="text-xs text-[#5C6359] mt-1" data-testid="twin-base-biomarker">
            {hasLabs ? <>Your actual: <strong className="text-[#2D322B]">{baseBiomarker ?? "—"}</strong></> : "Model baseline — add labs for your real reading"}
          </p>
        </div>
        <div className="rounded-2xl bg-[#2D322B] p-5 flex flex-col items-center" data-testid="twin-dial-adjusted">
          <div className="flex items-center gap-2 mb-1 text-[#F9F6F0]/70"><HeartPulse className="w-4 h-4 text-[#D49A44]" /><span className="text-xs uppercase tracking-widest">Lifestyle-adjusted</span></div>
          <div className="bg-[#F9F6F0] rounded-xl px-2 pt-1">
            <Dial value={adj} label="YOUR TWIN" color={toneColor(adj, chrono)} refValue={chrono} testid="twin-dial-adjusted-svg" />
          </div>
          <p className="text-xs mt-2" style={{ color: mod < 0 ? "#8FBF9F" : mod > 0 ? "#E9967A" : "#E4B564" }} data-testid="twin-modifier">
            {mod < 0 ? `${Math.abs(mod)} yrs younger` : mod > 0 ? `${mod} yrs older` : "on par"} from lifestyle
          </p>
        </div>
      </div>

      <p className="mt-4 text-sm text-[#5C6359] max-w-2xl" data-testid="twin-summary">
        {adjGap == null ? "Adjust the controls below to project your twin." :
          adjGap <= -1 ? `Your twin reads about ${Math.abs(adjGap)} years younger than the calendar — that's your potential.` :
          adjGap >= 1 ? `Your twin currently reads about ${adjGap} years older than the calendar — and every slider below is something you can change.` :
          "Your twin is closely aligned with your chronological age."}
      </p>

      {/* Controls */}
      <div className="mt-8 grid lg:grid-cols-2 gap-8">
        {/* Lifestyle habits */}
        <div>
          <p className="label-eyebrow mb-4">Lifestyle habits</p>
          <div className="space-y-4">
            {Object.keys(habitOptions).map((k) => (
              <div key={k} className="flex items-center justify-between gap-4" data-testid={`twin-habit-${k}`}>
                <label className="text-sm text-[#2D322B]">{HABIT_LABELS[k] || k}</label>
                <select
                  value={habits[k] || ""}
                  onChange={(e) => setHab(k, e.target.value)}
                  data-testid={`twin-habit-${k}-select`}
                  className="bg-[#F9F6F0] border border-[#D1C7B8] rounded-lg px-3 py-1.5 text-sm text-[#2D322B] focus:border-[#C05C42] outline-none min-w-[130px]"
                >
                  <option value="">Not set</option>
                  {habitOptions[k].map((opt) => <option key={opt} value={opt}>{optLabel(opt)}</option>)}
                </select>
              </div>
            ))}
          </div>

          <p className="label-eyebrow mt-8 mb-4">How you feel (1–5)</p>
          <div className="space-y-4">
            {SYMPTOMS.map((d) => (
              <div key={d.key} data-testid={`twin-symptom-${d.key}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm text-[#2D322B]">{d.label}</span>
                  <span className="text-sm font-semibold text-[#C05C42]">{symptoms[d.key]}</span>
                </div>
                <div className="flex gap-1.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" onClick={() => setSym(d.key, n)}
                      data-testid={`twin-symptom-${d.key}-${n}`}
                      className={`flex-1 h-8 rounded-md border text-xs transition-colors ${symptoms[d.key] === n ? "bg-[#C05C42] text-[#F9F6F0] border-[#C05C42]" : "bg-[#F9F6F0] text-[#5C6359] border-[#D1C7B8] hover:border-[#C05C42]"}`}>
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Biomarker sliders */}
        <div>
          <p className="label-eyebrow mb-4">Biomarkers <span className="normal-case tracking-normal text-[#5C6359]">(slide to model changes)</span></p>
          <div className="space-y-5">
            {phenoInputs.map((p) => (
              <div key={p.slot} data-testid={`twin-bio-${p.slot}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-[#2D322B]">{p.label} {!p.is_real && <span className="text-[10px] uppercase tracking-widest text-[#8a6620]">est.</span>}</span>
                  <span className="text-sm font-medium text-[#2D322B]">{Number(bio[p.slot]).toFixed(p.step < 1 ? 2 : 0)} <span className="text-[#5C6359] text-xs">{p.unit}</span></span>
                </div>
                <input type="range" min={p.min} max={p.max} step={p.step} value={bio[p.slot]}
                  onChange={(e) => setBioV(p.slot, parseFloat(e.target.value))}
                  data-testid={`twin-bio-${p.slot}-range`}
                  className="w-full accent-[#C05C42]" />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3 border-t border-[#D1C7B8] pt-6">
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Name this scenario (e.g. Quit smoking + sleep 7–8h)"
          data-testid="twin-save-label"
          className="flex-1 bg-[#F9F6F0] border border-[#D1C7B8] rounded-lg px-4 py-2.5 text-sm text-[#2D322B] focus:border-[#C05C42] outline-none"
        />
        <button onClick={saveTwin} disabled={saving} data-testid="twin-save" className="btn-primary inline-flex items-center gap-2 disabled:opacity-60 shrink-0">
          <Bookmark className="w-4 h-4" /> {saving ? "Saving…" : "Save this twin"}
        </button>
        <button onClick={reset} data-testid="twin-reset" className="btn-secondary inline-flex items-center gap-2 shrink-0">
          <RotateCcw className="w-4 h-4" /> Reset
        </button>
      </div>
    </div>
  );
}
